"use client";

import { useState, useEffect, useRef } from "react";
import { getSubdomain } from "@/utils/subdomain";

export function useDynamicLogo(defaultLogo = "/instacks-logo.png", explicitSubdomain?: string | null) {
  const [logo, setLogo] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const sub = explicitSubdomain || localStorage.getItem("subdomain") || getSubdomain();
      if (sub) {
        const cached = localStorage.getItem(`logo_${sub}`);
        if (cached && cached !== "/instacks-logo.png" && !cached.includes("instacks-logo")) {
          return cached;
        }
      }
      const cached_owner = localStorage.getItem("cached_owner_logo");
      if (cached_owner && cached_owner !== "/instacks-logo.png" && !cached_owner.includes("instacks-logo")) {
        return cached_owner;
      }
    }
    return defaultLogo;
  });
  const [isLoading, setIsLoading] = useState(false);
  // Use ref so all effect runs share latest value without stale closure
  const latestLogoRef = useRef<string>(logo);

  useEffect(() => {
    let cancelled = false;

    async function fetchLogo() {
      try {
        let sub = explicitSubdomain || localStorage.getItem("subdomain") || getSubdomain();
        if (!sub) {
          if (!cancelled) {
            latestLogoRef.current = defaultLogo;
            setLogo(defaultLogo);
            setIsLoading(false);
          }
          return;
        }

        const cached = localStorage.getItem(`logo_${sub}`);
        if (cached && cached !== "/instacks-logo.png" && !cached.includes("instacks-logo")) {
          if (!cancelled) {
            latestLogoRef.current = cached;
            setLogo(cached);
            setIsLoading(false);
          }
          return;
        }

        setIsLoading(true);

        let api_base_url = process.env.NEXT_PUBLIC_API_URL;
        if (!api_base_url && typeof window !== "undefined") {
          const is_local =
            window.location.hostname === "localhost" ||
            window.location.hostname.endsWith(".localhost") ||
            window.location.hostname === "127.0.0.1";
          if (!is_local) {
            api_base_url = `${window.location.origin}/api/v2`;
          }
        }
        if (!api_base_url) {
          api_base_url = "http://localhost:5100/api/v2";
        }

        const res = await fetch(`${api_base_url}/subdomain/logo/${encodeURIComponent(sub)}`);
        const data = await res.json();

        if (cancelled) return;

        if (
          data?.success &&
          data?.data?.logo &&
          data.data.logo !== "null" &&
          data.data.logo !== "undefined" &&
          data.data.logo !== "/instacks-logo.png" &&
          !data.data.logo.includes("instacks-logo")
        ) {
          const { getImageUrl } = await import("@/utils/image-url");
          const logoUrl = data.data.logo.startsWith("http")
            ? data.data.logo
            : (getImageUrl(data.data.logo) || defaultLogo);
          latestLogoRef.current = logoUrl;
          localStorage.setItem(`logo_${sub}`, logoUrl);
          setLogo(logoUrl);
        } else {
          latestLogoRef.current = defaultLogo;
          setLogo(defaultLogo);
        }
      } catch (error) {
        if (!cancelled) {
          latestLogoRef.current = defaultLogo;
          setLogo(defaultLogo);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchLogo();

    return () => {
      cancelled = true;
    };
  }, [defaultLogo, explicitSubdomain]);

  return { logo, isLoading, latestLogo: latestLogoRef.current };
}
